import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Button,
  Typography,
  Tabs,
  Tab,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  Checkbox,
  FormGroup,
  FormControlLabel,
  Alert,
  Chip,
  Stack,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  IconButton,
  Paper,
  Autocomplete,
  Slider,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ContentCopy as CopyIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  Person as PersonIcon,
  AutoAwesome as AIIcon,
  Info as InfoIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import axios from 'axios';
import toast from 'react-hot-toast';

function TabPanel({ children, value, index }) {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ py: 1.5 }}>{children}</Box>}
    </div>
  );
}

const PromptGeneratorPro = ({
  brandId,
  onPromptGenerated,
  embedded = false,
  onIdeogramGenerate,
  ideogramGenerating = false,
  canIdeogramGenerate = false,
  currentPromptValue,
  onPromptChange,
  negativePromptValue,
  onNegativePromptChange,
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generatedPrompt, setGeneratedPrompt] = useState(null);
  const [showPromptModal, setShowPromptModal] = useState(false);
  const [aiServerStatus, setAiServerStatus] = useState(null);

  // Characters state
  const [characters, setCharacters] = useState([
    { id: 1, name: 'Character 1', gender: 'female', age: '20s', build: '', pose: '', ethnicity: '', expression: '', hair: '', facialFeatures: '' }
  ]);

  // Form state
  const [formData, setFormData] = useState({
    // Project Setup
    campaignPurpose: 'drop_launch',
    targetDemographic: 'gen_z',
    catalogSource: 'master',
    brandSelector: '',
    creativeConcept: '',

    // Scene & Environment
    locationType: '',
    specificVenue: '',
    timeOfDay: 'golden_hour',
    weatherSeason: 'summer',
    environmentProps: [],

    // Lighting & Technical
    lightingSetup: 'natural',
    lightQuality: 'soft',
    colorTemperature: 'warm',
    lightingDirection: 'side',

    // Styling & Garments
    stylingApproach: 'minimal',
    garmentFit: 'tailored',
    layering: 'single',
    accessories: [],
    primaryGarment: '',
    supportingGarments: [],
    fabrics: [],
    sivadPieces: [],

    // Mood & Aesthetic
    overallMood: 'confident',
    colorPalette: 'neutral',
    aesthetic: 'contemporary',
    brandVibe: 'luxury',

    // Technical Specs
    photoStyle: 'editorial',
    cameraAngle: 'eye_level',
    composition: 'rule_of_thirds',
    depthOfField: 'shallow',
    postProcessing: 'clean_modern',
  });

  // Ideogram Configuration
  const [ideogramConfig, setIdeogramConfig] = useState({
    numImages: 1,
    qualitySteps: 25,
    guidanceScale: 7.5,
  });
  const isPromptControlled = typeof onPromptChange === 'function';
  const isNegativePromptControlled = typeof onNegativePromptChange === 'function';

  const [internalCurrentPrompt, setInternalCurrentPrompt] = useState(currentPromptValue ?? '');
  const [internalNegativePrompt, setInternalNegativePrompt] = useState(
    negativePromptValue ?? 'low quality, blurry, distorted, ugly'
  );

  useEffect(() => {
    if (isPromptControlled) {
      setInternalCurrentPrompt(currentPromptValue ?? '');
    }
  }, [isPromptControlled, currentPromptValue]);

  useEffect(() => {
    if (isNegativePromptControlled) {
      setInternalNegativePrompt(negativePromptValue ?? 'low quality, blurry, distorted, ugly');
    }
  }, [isNegativePromptControlled, negativePromptValue]);

  const currentPrompt = isPromptControlled
    ? currentPromptValue ?? internalCurrentPrompt
    : internalCurrentPrompt;

  const negativePrompt = isNegativePromptControlled
    ? negativePromptValue ?? internalNegativePrompt
    : internalNegativePrompt;

  const updateCurrentPrompt = (value) => {
    setInternalCurrentPrompt(value);
    if (onPromptChange) {
      onPromptChange(value);
    }
  };

  const updateNegativePrompt = (value) => {
    setInternalNegativePrompt(value);
    if (onNegativePromptChange) {
      onNegativePromptChange(value);
    }
  };

  const [apparelOptions, setApparelOptions] = useState([]);
  const [accessoryOptions, setAccessoryOptions] = useState([]);
  const [buildOptions, setBuildOptions] = useState([]);
  const [sceneOptions, setSceneOptions] = useState([]);
  const [propsOptions, setPropsOptions] = useState([]);
  const [poseOptions, setPoseOptions] = useState([]);
  const [fabricsOptions, setFabricsOptions] = useState([]);
  const [expressionOptions, setExpressionOptions] = useState([]);
  const [facialFeaturesOptions, setFacialFeaturesOptions] = useState([]);
  const [hairOptions, setHairOptions] = useState([]);
  const [sivadPiecesOptions, setSivadPiecesOptions] = useState([]);
  const [libraryLoading, setLibraryLoading] = useState({
    apparel: false,
    accessories: false,
    build: false,
    scene: false,
    props: false,
    pose: false,
    fabrics: false,
    expression: false,
    facialFeatures: false,
    hair: false,
    sivadPieces: false
  });
  const [libraryErrors, setLibraryErrors] = useState({});

  const handleChange = (field) => (event) => {
    setFormData({ ...formData, [field]: event.target.value });
  };

  const handleMultiSelectChange = (field, value) => {
    setFormData((prev) => {
      const current = prev[field] || [];
      const newValues = current.includes(value)
        ? current.filter((v) => v !== value)
        : [...current, value];
      return { ...prev, [field]: newValues };
    });
  };

  const handleCharacterChange = (characterId, field, value) => {
    setCharacters((prev) =>
      prev.map((char) =>
        char.id === characterId ? { ...char, [field]: value } : char
      )
    );
  };

  const addCharacter = () => {
    const newId = Math.max(...characters.map((c) => c.id), 0) + 1;
    setCharacters([
      ...characters,
      { id: newId, name: `Character ${newId}`, gender: 'female', age: '20s', build: '', pose: '', ethnicity: '', expression: '', hair: '', facialFeatures: '' }
    ]);
  };

  const removeCharacter = (characterId) => {
    if (characters.length > 1) {
      setCharacters((prev) => prev.filter((char) => char.id !== characterId));
    }
  };

  const mapLibraryEntries = useCallback(
    (entries = []) =>
      entries
        .map((entry) => ({
          key: entry.key,
          name: entry.name || entry.key,
          preview: entry.preview,
          data: entry.data,
        }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    []
  );

  // Check AI server health on mount
  useEffect(() => {
    checkAIServerHealth();
  }, []);

  const checkAIServerHealth = async () => {
    try {
      const response = await axios.get('/api/design-studio/health');
      setAiServerStatus(response.data.success ? 'online' : 'offline');
    } catch (error) {
      console.error('AI server health check failed:', error);
      setAiServerStatus('offline');
    }
  };

  useEffect(() => {
    let isMounted = true;

    const loadLibrary = async (libraryKey, setter, loadingKey) => {
      setLibraryLoading((prev) => ({ ...prev, [loadingKey]: true }));
      try {
        const { data } = await axios.get(`/api/prompt-generator/library/${libraryKey}`, {
          params: { limit: 400 },
        });
        if (!isMounted) return;
        setter(mapLibraryEntries(data.entries || []));
        setLibraryErrors((prev) => ({ ...prev, [loadingKey]: null }));
      } catch (error) {
        console.error(`Failed to load ${libraryKey} library`, error);
        if (!isMounted) return;
        setLibraryErrors((prev) => ({
          ...prev,
          [loadingKey]: 'Failed to load design library entries',
        }));
        toast.error(`Unable to load ${libraryKey.replace(/-/g, ' ')} selections`);
      } finally {
        if (isMounted) {
          setLibraryLoading((prev) => ({ ...prev, [loadingKey]: false }));
        }
      }
    };

    loadLibrary('apparellibrary', setApparelOptions, 'apparel');
    loadLibrary('accessorylibrary', setAccessoryOptions, 'accessories');
    loadLibrary('buildlibrary', setBuildOptions, 'build');
    loadLibrary('scenelibrary', setSceneOptions, 'scene');
    loadLibrary('propslibrary', setPropsOptions, 'props');
    loadLibrary('poselibrary', setPoseOptions, 'pose');
    loadLibrary('fabricslibrary', setFabricsOptions, 'fabrics');
    loadLibrary('expressionlibrary', setExpressionOptions, 'expression');
    loadLibrary('facialfeatureslibrary', setFacialFeaturesOptions, 'facialFeatures');
    loadLibrary('hairlibrary', setHairOptions, 'hair');
    loadLibrary('sivadpieceslibrary', setSivadPiecesOptions, 'sivadPieces');

    return () => {
      isMounted = false;
    };
  }, [brandId, mapLibraryEntries]);

  const primaryGarmentOption = useMemo(
    () => apparelOptions.find((option) => option.key === formData.primaryGarment) || null,
    [apparelOptions, formData.primaryGarment]
  );

  const supportingGarmentOptions = useMemo(
    () =>
      (formData.supportingGarments || [])
        .map(key => apparelOptions.find(option => option.key === key))
        .filter(Boolean),
    [apparelOptions, formData.supportingGarments]
  );

  const accessorySelectionOptions = useMemo(
    () =>
      (formData.accessories || [])
        .map(key => accessoryOptions.find(option => option.key === key))
        .filter(Boolean),
    [accessoryOptions, formData.accessories]
  );

  const generatePrompt = async () => {
    try {
      setGenerating(true);

      const promptData = {
        ...formData,
        characters,
        brandId,
        librarySelections: {
          apparel: {
            primary: primaryGarmentOption,
            supporting: supportingGarmentOptions,
          },
          accessories: accessorySelectionOptions,
        },
      };

      const response = await axios.post('/api/prompt-generator/generate-pro', promptData);

      setGeneratedPrompt(response.data);
      setShowPromptModal(true);

      // Auto-populate the prompt field with generated prompt
      const promptText = response.data.ideogram_prompt || response.data.narrative ||
        (typeof response.data.prompt === 'string' ? response.data.prompt : JSON.stringify(response.data.prompt));
      updateCurrentPrompt(promptText);

      // Call the callback if provided (for Design Studio integration)
      if (onPromptGenerated && response.data) {
        onPromptGenerated({
          ...response.data,
          ideogramConfig,
          currentPrompt: promptText,
          negativePrompt,
        });
      }
    } catch (error) {
      console.error('Error generating prompt:', error);
      toast.error('Failed to generate prompt');
    } finally {
      setGenerating(false);
    }
  };

  const copyPrompt = () => {
    if (generatedPrompt) {
      const text = typeof generatedPrompt.prompt === 'string'
        ? generatedPrompt.prompt
        : JSON.stringify(generatedPrompt.prompt, null, 2);
      navigator.clipboard.writeText(text);
      toast.success('Prompt copied to clipboard');
    }
  };

  const handleReset = () => {
    // Reset all form data to initial state
    setFormData({
      campaignPurpose: 'drop_launch',
      targetDemographic: 'gen_z',
      catalogSource: 'master',
      brandSelector: '',
      creativeConcept: '',
      locationType: '',
      specificVenue: '',
      timeOfDay: 'golden_hour',
      weatherSeason: 'summer',
      environmentProps: [],
      lightingSetup: 'natural',
      lightQuality: 'soft',
      colorTemperature: 'warm',
      lightingDirection: 'side',
      stylingApproach: 'minimal',
      garmentFit: 'tailored',
      layering: 'single',
      accessories: [],
      primaryGarment: '',
      supportingGarments: [],
      fabrics: [],
      sivadPieces: [],
      overallMood: 'confident',
      colorPalette: 'neutral',
      aesthetic: 'contemporary',
      brandVibe: 'luxury',
      photoStyle: 'editorial',
      cameraAngle: 'eye_level',
      composition: 'rule_of_thirds',
      depthOfField: 'shallow',
      postProcessing: 'clean_modern',
    });

    // Reset characters to single default character
    setCharacters([
      { id: 1, name: 'Character 1', gender: 'female', age: '20s', build: '', pose: '', ethnicity: '', expression: '', hair: '', facialFeatures: '' }
    ]);

    // Reset Ideogram config
    setIdeogramConfig({
      numImages: 1,
      qualitySteps: 25,
      guidanceScale: 7.5,
    });

    // Clear prompts
    updateCurrentPrompt('');
    updateNegativePrompt('low quality, blurry, distorted, ugly');

    // Clear generated prompt and modal
    setGeneratedPrompt(null);
    setShowPromptModal(false);

    // Reset to first tab
    setActiveTab(0);

    toast.success('Prompt generator reset');
  };

  const tabs = [
    { label: 'Ideogram Config', value: 0 },
    { label: 'Project Setup', value: 1 },
    { label: 'Scene & Environment', value: 2 },
    { label: 'Lighting & Technical', value: 3 },
    { label: 'Models & Characters', value: 4 },
    { label: 'Styling & Garments', value: 5 },
    { label: 'Mood & Aesthetic', value: 6 },
    { label: 'Technical Specs', value: 7 },
  ];

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Header - Only show when not embedded */}
      {!embedded && (
        <Box sx={{ mb: 4, flexShrink: 0 }}>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            Professional AI Prompt Generator
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Create detailed prompts with professional photography controls and comprehensive styling options
          </Typography>
        </Box>
      )}

      {/* Main Card */}
      <Card elevation={embedded ? 0 : undefined} sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, minHeight: 0, overflow: 'hidden' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <Tabs
            value={activeTab}
            onChange={(e, v) => setActiveTab(v)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ minHeight: 42, flexGrow: 1 }}
          >
            {tabs.map((tab) => (
              <Tab key={tab.value} label={tab.label} sx={{ minHeight: 42, py: 0.5 }} />
            ))}
          </Tabs>
          <Button
            variant="outlined"
            size="small"
            onClick={handleReset}
            disabled={generating || ideogramGenerating}
            startIcon={<RefreshIcon />}
            sx={{ mr: 2, minWidth: 100 }}
            color="secondary"
          >
            Reset
          </Button>
        </Box>

        <CardContent sx={{ p: embedded ? 1 : 2, flexGrow: 1, overflow: 'auto', minHeight: 0 }}>
          {/* Ideogram Config Tab */}
          <TabPanel value={activeTab} index={0}>
            <Alert severity="info" sx={{ mb: 2 }}>
              Configure Ideogram v3 Turbo generation settings. These settings control the prompt, number of images generated, quality, and guidance strength.
            </Alert>
            <Grid container spacing={2} alignItems="flex-start">
              {/* Prompt Field */}
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                  Prompt
                </Typography>
                <TextField
                  placeholder="Type or paste a prompt, or generate one using the tabs below..."
                  value={currentPrompt}
                  onChange={(event) => updateCurrentPrompt(event.target.value)}
                  multiline
                  minRows={3}
                  maxRows={6}
                  fullWidth
                  size="small"
                  InputProps={{ sx: { fontSize: '0.85rem', lineHeight: 1.4 } }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  Ideogram-optimized prompt. Edit as needed or generate using the Project Setup tab.
                </Typography>
              </Grid>

              {/* Negative Prompt Field */}
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                  Negative Prompt
                </Typography>
                <TextField
                  placeholder="Things to avoid in the generation..."
                  value={negativePrompt}
                  onChange={(event) => updateNegativePrompt(event.target.value)}
                  multiline
                  minRows={3}
                  maxRows={6}
                  fullWidth
                  size="small"
                  InputProps={{ sx: { fontSize: '0.85rem', lineHeight: 1.4 } }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  Elements to avoid in generation (improves quality).
                </Typography>
              </Grid>

              <Grid item xs={12} md={4}>
                <Box sx={{ px: { xs: 0, md: 1 } }}>
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                    Number of Images
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {ideogramConfig.numImages} image{ideogramConfig.numImages > 1 ? 's' : ''} will be generated
                  </Typography>
                  <Slider
                    value={ideogramConfig.numImages}
                    onChange={(e, v) => setIdeogramConfig({ ...ideogramConfig, numImages: v })}
                    min={1}
                    max={4}
                    marks
                    step={1}
                    valueLabelDisplay="auto"
                  />
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                    More images = more options, but higher cost (~$0.03 per image)
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} md={4}>
                <Box sx={{ px: { xs: 0, md: 1 } }}>
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                    Quality Steps
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {ideogramConfig.qualitySteps} inference steps
                  </Typography>
                  <Slider
                    value={ideogramConfig.qualitySteps}
                    onChange={(e, v) => setIdeogramConfig({ ...ideogramConfig, qualitySteps: v })}
                    min={10}
                    max={50}
                    step={5}
                    marks={[
                      { value: 10, label: '10' },
                      { value: 25, label: '25' },
                      { value: 50, label: '50' },
                    ]}
                    valueLabelDisplay="auto"
                  />
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                    Higher steps = better quality, but slower generation
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} md={4}>
                <Box sx={{ px: { xs: 0, md: 1 } }}>
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                    Guidance Scale
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Scale: {ideogramConfig.guidanceScale}
                  </Typography>
                  <Slider
                    value={ideogramConfig.guidanceScale}
                    onChange={(e, v) => setIdeogramConfig({ ...ideogramConfig, guidanceScale: v })}
                    min={1}
                    max={15}
                    step={0.5}
                    marks={[
                      { value: 1, label: '1' },
                      { value: 7.5, label: '7.5' },
                      { value: 15, label: '15' },
                    ]}
                    valueLabelDisplay="auto"
                  />
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                    Higher guidance = stricter adherence to prompt
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </TabPanel>

          {/* Project Setup Tab */}
          <TabPanel value={activeTab} index={1}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Campaign Purpose</InputLabel>
                  <Select
                    value={formData.campaignPurpose}
                    onChange={handleChange('campaignPurpose')}
                    label="Campaign Purpose"
                  >
                    <MenuItem value="drop_launch">Drop Launch</MenuItem>
                    <MenuItem value="brand_content">Brand Content</MenuItem>
                    <MenuItem value="branding_campaign">Branding Campaign</MenuItem>
                    <MenuItem value="lookbook">Lookbook</MenuItem>
                    <MenuItem value="editorial_story">Editorial Story</MenuItem>
                    <MenuItem value="social_media">Social Media</MenuItem>
                    <MenuItem value="ecommerce">E-commerce</MenuItem>
                    <MenuItem value="advertising">Advertising Campaign</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Target Demographic</InputLabel>
                  <Select
                    value={formData.targetDemographic}
                    onChange={handleChange('targetDemographic')}
                    label="Target Demographic"
                  >
                    <MenuItem value="gen_z">Gen Z (18-26)</MenuItem>
                    <MenuItem value="millennial">Millennial (27-42)</MenuItem>
                    <MenuItem value="gen_x">Gen X (43-58)</MenuItem>
                    <MenuItem value="luxury_consumer">Luxury Consumer</MenuItem>
                    <MenuItem value="streetwear_enthusiast">Streetwear Enthusiast</MenuItem>
                    <MenuItem value="athleisure">Athleisure Market</MenuItem>
                    <MenuItem value="professional">Professional/Corporate</MenuItem>
                    <MenuItem value="creative_professional">Creative Professional</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Catalog Source</InputLabel>
                  <Select
                    value={formData.catalogSource}
                    onChange={handleChange('catalogSource')}
                    label="Catalog Source"
                  >
                    <MenuItem value="master">Master SIVAD Libraries</MenuItem>
                    <MenuItem value="brand">Brand Catalogs</MenuItem>
                    <MenuItem value="combined">Combined (Master + Brand)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Creative Concept"
                  value={formData.creativeConcept}
                  onChange={handleChange('creativeConcept')}
                  placeholder="Overall creative vision, mood, or story concept..."
                />
              </Grid>
            </Grid>
          </TabPanel>

          {/* Scene & Environment Tab */}
          <TabPanel value={activeTab} index={2}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Scene/Location</InputLabel>
                  <Select
                    value={formData.locationType}
                    onChange={handleChange('locationType')}
                    label="Scene/Location"
                    disabled={libraryLoading.scene}
                  >
                    {libraryLoading.scene && (
                      <MenuItem value="" disabled>
                        <CircularProgress size={16} /> Loading scenes...
                      </MenuItem>
                    )}
                    {!libraryLoading.scene && sceneOptions.length === 0 && (
                      <MenuItem value="" disabled>No scenes available</MenuItem>
                    )}
                    {sceneOptions.map((option) => (
                      <MenuItem key={option.key} value={option.key}>
                        {option.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Time of Day</InputLabel>
                  <Select
                    value={formData.timeOfDay}
                    onChange={handleChange('timeOfDay')}
                    label="Time of Day"
                  >
                    <MenuItem value="golden_hour">Golden Hour</MenuItem>
                    <MenuItem value="midday">Midday</MenuItem>
                    <MenuItem value="overcast">Overcast</MenuItem>
                    <MenuItem value="evening">Evening</MenuItem>
                    <MenuItem value="night">Night</MenuItem>
                    <MenuItem value="blue_hour">Blue Hour</MenuItem>
                    <MenuItem value="sunrise">Sunrise</MenuItem>
                    <MenuItem value="sunset">Sunset</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Weather/Season</InputLabel>
                  <Select
                    value={formData.weatherSeason}
                    onChange={handleChange('weatherSeason')}
                    label="Weather/Season"
                  >
                    <MenuItem value="spring">Spring</MenuItem>
                    <MenuItem value="summer">Summer</MenuItem>
                    <MenuItem value="autumn">Autumn</MenuItem>
                    <MenuItem value="winter">Winter</MenuItem>
                    <MenuItem value="rainy">Rainy</MenuItem>
                    <MenuItem value="windy">Windy</MenuItem>
                    <MenuItem value="snow">Snow</MenuItem>
                    <MenuItem value="foggy">Foggy</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <Autocomplete
                  multiple
                  options={propsOptions}
                  getOptionLabel={(option) => option.name}
                  value={propsOptions.filter((opt) => formData.environmentProps.includes(opt.key))}
                  onChange={(e, newValue) => {
                    setFormData({ ...formData, environmentProps: newValue.map((v) => v.key) });
                  }}
                  loading={libraryLoading.props}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Props & Set Elements"
                      placeholder={libraryLoading.props ? "Loading props..." : "Select props"}
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        key={option.key}
                        label={option.name}
                        size="small"
                        {...getTagProps({ index })}
                      />
                    ))
                  }
                />
              </Grid>
            </Grid>
          </TabPanel>

          {/* Lighting & Technical Tab */}
          <TabPanel value={activeTab} index={3}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Lighting Setup</InputLabel>
                  <Select
                    value={formData.lightingSetup}
                    onChange={handleChange('lightingSetup')}
                    label="Lighting Setup"
                  >
                    <MenuItem value="natural">Natural Light</MenuItem>
                    <MenuItem value="studio">Studio Lighting</MenuItem>
                    <MenuItem value="mixed">Mixed Lighting</MenuItem>
                    <MenuItem value="dramatic">Dramatic</MenuItem>
                    <MenuItem value="soft_diffused">Soft Diffused</MenuItem>
                    <MenuItem value="hard_direct">Hard Direct</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Light Quality</InputLabel>
                  <Select
                    value={formData.lightQuality}
                    onChange={handleChange('lightQuality')}
                    label="Light Quality"
                  >
                    <MenuItem value="soft">Soft</MenuItem>
                    <MenuItem value="hard">Hard</MenuItem>
                    <MenuItem value="diffused">Diffused</MenuItem>
                    <MenuItem value="directional">Directional</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Color Temperature</InputLabel>
                  <Select
                    value={formData.colorTemperature}
                    onChange={handleChange('colorTemperature')}
                    label="Color Temperature"
                  >
                    <MenuItem value="warm">Warm</MenuItem>
                    <MenuItem value="cool">Cool</MenuItem>
                    <MenuItem value="neutral">Neutral</MenuItem>
                    <MenuItem value="mixed">Mixed</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Lighting Direction</InputLabel>
                  <Select
                    value={formData.lightingDirection}
                    onChange={handleChange('lightingDirection')}
                    label="Lighting Direction"
                  >
                    <MenuItem value="front">Front</MenuItem>
                    <MenuItem value="side">Side</MenuItem>
                    <MenuItem value="back">Back</MenuItem>
                    <MenuItem value="top">Top</MenuItem>
                    <MenuItem value="bottom">Bottom</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </TabPanel>

          {/* Models & Characters Tab */}
          <TabPanel value={activeTab} index={4}>
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">Characters/Models</Typography>
              <Button startIcon={<AddIcon />} onClick={addCharacter} variant="outlined">
                Add Character
              </Button>
            </Box>

            <Stack spacing={2}>
              {characters.map((character, index) => (
                <Paper key={character.id} sx={{ p: 2, border: '1px solid', borderColor: 'divider' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <PersonIcon />
                      <Typography variant="subtitle1" fontWeight={600}>
                        {character.name}
                      </Typography>
                    </Stack>
                    {characters.length > 1 && (
                      <IconButton size="small" onClick={() => removeCharacter(character.id)} color="error">
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </Box>

                  <Grid container spacing={2}>
                    <Grid item xs={12} md={3}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Name"
                        value={character.name}
                        onChange={(e) => handleCharacterChange(character.id, 'name', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Gender</InputLabel>
                        <Select
                          value={character.gender}
                          onChange={(e) => handleCharacterChange(character.id, 'gender', e.target.value)}
                          label="Gender"
                        >
                          <MenuItem value="male">Male</MenuItem>
                          <MenuItem value="female">Female</MenuItem>
                          <MenuItem value="non_binary">Non-Binary</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Age Range</InputLabel>
                        <Select
                          value={character.age}
                          onChange={(e) => handleCharacterChange(character.id, 'age', e.target.value)}
                          label="Age Range"
                        >
                          <MenuItem value="teens">Teens</MenuItem>
                          <MenuItem value="20s">20s</MenuItem>
                          <MenuItem value="30s">30s</MenuItem>
                          <MenuItem value="40s">40s</MenuItem>
                          <MenuItem value="50s+">50s+</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Build</InputLabel>
                        <Select
                          value={character.build}
                          onChange={(e) => handleCharacterChange(character.id, 'build', e.target.value)}
                          label="Build"
                          disabled={libraryLoading.build}
                        >
                          {libraryLoading.build && (
                            <MenuItem value="" disabled>
                              <CircularProgress size={16} /> Loading builds...
                            </MenuItem>
                          )}
                          {!libraryLoading.build && buildOptions.length === 0 && (
                            <MenuItem value="" disabled>No builds available</MenuItem>
                          )}
                          {buildOptions.map((option) => (
                            <MenuItem key={option.key} value={option.key}>
                              {option.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Pose</InputLabel>
                        <Select
                          value={character.pose}
                          onChange={(e) => handleCharacterChange(character.id, 'pose', e.target.value)}
                          label="Pose"
                          disabled={libraryLoading.pose}
                        >
                          {libraryLoading.pose && (
                            <MenuItem value="" disabled>
                              <CircularProgress size={16} /> Loading poses...
                            </MenuItem>
                          )}
                          {!libraryLoading.pose && poseOptions.length === 0 && (
                            <MenuItem value="" disabled>No poses available</MenuItem>
                          )}
                          {poseOptions.map((option) => (
                            <MenuItem key={option.key} value={option.key}>
                              {option.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Expression</InputLabel>
                        <Select
                          value={character.expression}
                          onChange={(e) => handleCharacterChange(character.id, 'expression', e.target.value)}
                          label="Expression"
                          disabled={libraryLoading.expression}
                        >
                          {libraryLoading.expression && (
                            <MenuItem value="" disabled>
                              <CircularProgress size={16} /> Loading expressions...
                            </MenuItem>
                          )}
                          {!libraryLoading.expression && expressionOptions.length === 0 && (
                            <MenuItem value="" disabled>No expressions available</MenuItem>
                          )}
                          {expressionOptions.map((option) => (
                            <MenuItem key={option.key} value={option.key}>
                              {option.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Hair</InputLabel>
                        <Select
                          value={character.hair}
                          onChange={(e) => handleCharacterChange(character.id, 'hair', e.target.value)}
                          label="Hair"
                          disabled={libraryLoading.hair}
                        >
                          {libraryLoading.hair && (
                            <MenuItem value="" disabled>
                              <CircularProgress size={16} /> Loading hair styles...
                            </MenuItem>
                          )}
                          {!libraryLoading.hair && hairOptions.length === 0 && (
                            <MenuItem value="" disabled>No hair styles available</MenuItem>
                          )}
                          {hairOptions.map((option) => (
                            <MenuItem key={option.key} value={option.key}>
                              {option.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Facial Features</InputLabel>
                        <Select
                          value={character.facialFeatures}
                          onChange={(e) => handleCharacterChange(character.id, 'facialFeatures', e.target.value)}
                          label="Facial Features"
                          disabled={libraryLoading.facialFeatures}
                        >
                          {libraryLoading.facialFeatures && (
                            <MenuItem value="" disabled>
                              <CircularProgress size={16} /> Loading features...
                            </MenuItem>
                          )}
                          {!libraryLoading.facialFeatures && facialFeaturesOptions.length === 0 && (
                            <MenuItem value="" disabled>No features available</MenuItem>
                          )}
                          {facialFeaturesOptions.map((option) => (
                            <MenuItem key={option.key} value={option.key}>
                              {option.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Ethnicity/Additional Features"
                        value={character.ethnicity}
                        onChange={(e) => handleCharacterChange(character.id, 'ethnicity', e.target.value)}
                        placeholder="Optional: Specific ethnic features or other characteristics..."
                      />
                    </Grid>
                  </Grid>
                </Paper>
              ))}
            </Stack>
          </TabPanel>

          {/* Styling & Garments Tab */}
          <TabPanel value={activeTab} index={5}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Styling Approach</InputLabel>
                  <Select
                    value={formData.stylingApproach}
                    onChange={handleChange('stylingApproach')}
                    label="Styling Approach"
                  >
                    <MenuItem value="minimal">Minimal</MenuItem>
                    <MenuItem value="maximalist">Maximalist</MenuItem>
                    <MenuItem value="layered">Layered</MenuItem>
                    <MenuItem value="clean">Clean</MenuItem>
                    <MenuItem value="eclectic">Eclectic</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Garment Fit</InputLabel>
                  <Select
                    value={formData.garmentFit}
                    onChange={handleChange('garmentFit')}
                    label="Garment Fit"
                  >
                    <MenuItem value="oversized">Oversized</MenuItem>
                    <MenuItem value="relaxed">Relaxed</MenuItem>
                    <MenuItem value="tailored">Tailored</MenuItem>
                    <MenuItem value="fitted">Fitted</MenuItem>
                    <MenuItem value="mixed">Mixed</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Layering</InputLabel>
                  <Select
                    value={formData.layering}
                    onChange={handleChange('layering')}
                    label="Layering"
                  >
                    <MenuItem value="single">Single Layer</MenuItem>
                    <MenuItem value="light">Light Layering</MenuItem>
                    <MenuItem value="medium">Medium Layering</MenuItem>
                    <MenuItem value="heavy">Heavy Layering</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <Autocomplete
                  options={apparelOptions}
                  loading={libraryLoading.apparel}
                  value={primaryGarmentOption}
                  onChange={(_, value) =>
                    setFormData((prev) => ({
                      ...prev,
                      primaryGarment: value ? value.key : '',
                    }))
                  }
                  isOptionEqualToValue={(option, value) => option.key === value?.key}
                  getOptionLabel={(option) => option?.name || ''}
                  renderOption={(props, option) => (
                    <li {...props}>
                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography variant="body2" fontWeight={600}>
                          {option.name}
                        </Typography>
                        {option.preview && (
                          <Typography variant="caption" color="text.secondary">
                            {option.preview}
                          </Typography>
                        )}
                      </Box>
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Primary Garment"
                      placeholder="Select hero garment from YAML library"
                      size="small"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {libraryLoading.apparel ? <CircularProgress color="inherit" size={16} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  Pulled from ApparelLibrary_Master.yaml
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <Autocomplete
                  multiple
                  filterSelectedOptions
                  options={apparelOptions}
                  loading={libraryLoading.apparel}
                  value={supportingGarmentOptions}
                  onChange={(_, values) =>
                    setFormData((prev) => ({
                      ...prev,
                      supportingGarments: values.map((item) => item.key),
                    }))
                  }
                  isOptionEqualToValue={(option, value) => option.key === value?.key}
                  getOptionLabel={(option) => option?.name || ''}
                  renderOption={(props, option) => (
                    <li {...props}>
                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography variant="body2" fontWeight={600}>
                          {option.name}
                        </Typography>
                        {option.preview && (
                          <Typography variant="caption" color="text.secondary">
                            {option.preview}
                          </Typography>
                        )}
                      </Box>
                    </li>
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip {...getTagProps({ index })} key={option.key} label={option.name} size="small" />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Supporting Garments"
                      placeholder="Layer pieces or bottoms"
                      size="small"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {libraryLoading.apparel ? <CircularProgress color="inherit" size={16} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Autocomplete
                  multiple
                  filterSelectedOptions
                  options={accessoryOptions}
                  loading={libraryLoading.accessories}
                  value={accessorySelectionOptions}
                  onChange={(_, values) =>
                    setFormData((prev) => ({
                      ...prev,
                      accessories: values.map((item) => item.key),
                    }))
                  }
                  isOptionEqualToValue={(option, value) => option.key === value?.key}
                  getOptionLabel={(option) => option?.name || ''}
                  renderOption={(props, option) => (
                    <li {...props}>
                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography variant="body2" fontWeight={600}>
                          {option.name}
                        </Typography>
                        {option.preview && (
                          <Typography variant="caption" color="text.secondary">
                            {option.preview}
                          </Typography>
                        )}
                      </Box>
                    </li>
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip {...getTagProps({ index })} key={option.key} label={option.name} size="small" />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Accessories"
                      placeholder="Select accessories from library"
                      size="small"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {libraryLoading.accessories ? <CircularProgress color="inherit" size={16} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Autocomplete
                  multiple
                  filterSelectedOptions
                  options={fabricsOptions}
                  loading={libraryLoading.fabrics}
                  value={fabricsOptions.filter((opt) => formData.fabrics.includes(opt.key))}
                  onChange={(e, newValue) => {
                    setFormData({ ...formData, fabrics: newValue.map((v) => v.key) });
                  }}
                  isOptionEqualToValue={(option, value) => option.key === value?.key}
                  getOptionLabel={(option) => option?.name || ''}
                  renderOption={(props, option) => (
                    <li {...props}>
                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography variant="body2" fontWeight={600}>
                          {option.name}
                        </Typography>
                        {option.preview && (
                          <Typography variant="caption" color="text.secondary">
                            {option.preview}
                          </Typography>
                        )}
                      </Box>
                    </li>
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip {...getTagProps({ index })} key={option.key} label={option.name} size="small" />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Fabrics/Materials"
                      placeholder="Select fabrics from library"
                      size="small"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {libraryLoading.fabrics ? <CircularProgress color="inherit" size={16} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Autocomplete
                  multiple
                  filterSelectedOptions
                  options={sivadPiecesOptions}
                  loading={libraryLoading.sivadPieces}
                  value={sivadPiecesOptions.filter((opt) => formData.sivadPieces.includes(opt.key))}
                  onChange={(e, newValue) => {
                    setFormData({ ...formData, sivadPieces: newValue.map((v) => v.key) });
                  }}
                  isOptionEqualToValue={(option, value) => option.key === value?.key}
                  getOptionLabel={(option) => option?.name || ''}
                  renderOption={(props, option) => (
                    <li {...props}>
                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography variant="body2" fontWeight={600}>
                          {option.name}
                        </Typography>
                        {option.preview && (
                          <Typography variant="caption" color="text.secondary">
                            {option.preview}
                          </Typography>
                        )}
                      </Box>
                    </li>
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip {...getTagProps({ index })} key={option.key} label={option.name} size="small" />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="SIVAD Signature Pieces"
                      placeholder="Select SIVAD brand pieces"
                      size="small"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {libraryLoading.sivadPieces ? <CircularProgress color="inherit" size={16} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>

              {(libraryErrors.apparel || libraryErrors.accessories) && (
                <Grid item xs={12}>
                  <Alert severity="warning" sx={{ fontSize: '0.8rem' }}>
                    {libraryErrors.apparel || libraryErrors.accessories}
                  </Alert>
                </Grid>
              )}
            </Grid>
          </TabPanel>

          {/* Mood & Aesthetic Tab */}
          <TabPanel value={activeTab} index={6}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Overall Mood</InputLabel>
                  <Select
                    value={formData.overallMood}
                    onChange={handleChange('overallMood')}
                    label="Overall Mood"
                  >
                    <MenuItem value="confident">Confident</MenuItem>
                    <MenuItem value="relaxed">Relaxed</MenuItem>
                    <MenuItem value="energetic">Energetic</MenuItem>
                    <MenuItem value="sophisticated">Sophisticated</MenuItem>
                    <MenuItem value="playful">Playful</MenuItem>
                    <MenuItem value="dramatic">Dramatic</MenuItem>
                    <MenuItem value="mysterious">Mysterious</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Color Palette</InputLabel>
                  <Select
                    value={formData.colorPalette}
                    onChange={handleChange('colorPalette')}
                    label="Color Palette"
                  >
                    <MenuItem value="neutral">Neutral Tones</MenuItem>
                    <MenuItem value="monochrome">Monochrome</MenuItem>
                    <MenuItem value="vibrant">Vibrant Colors</MenuItem>
                    <MenuItem value="pastel">Pastel</MenuItem>
                    <MenuItem value="earth">Earth Tones</MenuItem>
                    <MenuItem value="jewel">Jewel Tones</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Aesthetic</InputLabel>
                  <Select
                    value={formData.aesthetic}
                    onChange={handleChange('aesthetic')}
                    label="Aesthetic"
                  >
                    <MenuItem value="contemporary">Contemporary</MenuItem>
                    <MenuItem value="vintage">Vintage</MenuItem>
                    <MenuItem value="minimalist">Minimalist</MenuItem>
                    <MenuItem value="avant_garde">Avant-garde</MenuItem>
                    <MenuItem value="streetwear">Streetwear</MenuItem>
                    <MenuItem value="luxury">Luxury</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Brand Vibe</InputLabel>
                  <Select
                    value={formData.brandVibe}
                    onChange={handleChange('brandVibe')}
                    label="Brand Vibe"
                  >
                    <MenuItem value="luxury">Luxury</MenuItem>
                    <MenuItem value="accessible">Accessible</MenuItem>
                    <MenuItem value="edgy">Edgy</MenuItem>
                    <MenuItem value="classic">Classic</MenuItem>
                    <MenuItem value="innovative">Innovative</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </TabPanel>

          {/* Technical Specs Tab */}
          <TabPanel value={activeTab} index={7}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Photography Style</InputLabel>
                  <Select
                    value={formData.photoStyle}
                    onChange={handleChange('photoStyle')}
                    label="Photography Style"
                  >
                    <MenuItem value="editorial">Editorial</MenuItem>
                    <MenuItem value="commercial">Commercial</MenuItem>
                    <MenuItem value="lifestyle">Lifestyle</MenuItem>
                    <MenuItem value="fashion_portrait">Fashion Portrait</MenuItem>
                    <MenuItem value="street_style">Street Style</MenuItem>
                    <MenuItem value="studio">Studio</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Camera Angle</InputLabel>
                  <Select
                    value={formData.cameraAngle}
                    onChange={handleChange('cameraAngle')}
                    label="Camera Angle"
                  >
                    <MenuItem value="eye_level">Eye Level</MenuItem>
                    <MenuItem value="high_angle">High Angle</MenuItem>
                    <MenuItem value="low_angle">Low Angle</MenuItem>
                    <MenuItem value="birds_eye">Bird's Eye</MenuItem>
                    <MenuItem value="dutch_angle">Dutch Angle</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Composition</InputLabel>
                  <Select
                    value={formData.composition}
                    onChange={handleChange('composition')}
                    label="Composition"
                  >
                    <MenuItem value="rule_of_thirds">Rule of Thirds</MenuItem>
                    <MenuItem value="centered">Centered</MenuItem>
                    <MenuItem value="symmetrical">Symmetrical</MenuItem>
                    <MenuItem value="leading_lines">Leading Lines</MenuItem>
                    <MenuItem value="frame_within_frame">Frame within Frame</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Depth of Field</InputLabel>
                  <Select
                    value={formData.depthOfField}
                    onChange={handleChange('depthOfField')}
                    label="Depth of Field"
                  >
                    <MenuItem value="shallow">Shallow (Blurred Background)</MenuItem>
                    <MenuItem value="medium">Medium</MenuItem>
                    <MenuItem value="deep">Deep (Everything in Focus)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Post-Processing</InputLabel>
                  <Select
                    value={formData.postProcessing}
                    onChange={handleChange('postProcessing')}
                    label="Post-Processing"
                  >
                    <MenuItem value="clean_modern">Clean Modern</MenuItem>
                    <MenuItem value="vintage_film">Vintage Film Look</MenuItem>
                    <MenuItem value="high_contrast">High Contrast</MenuItem>
                    <MenuItem value="muted_tones">Muted Tones</MenuItem>
                    <MenuItem value="vibrant_saturated">Vibrant Saturated</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </TabPanel>
        </CardContent>

        {/* Generate Button Section */}
        <Box
          sx={{
            position: 'sticky',
            bottom: 0,
            borderTop: 1,
            borderColor: 'divider',
            bgcolor: embedded ? 'background.paper' : 'background.default',
            flexShrink: 0,
            p: embedded ? 1.5 : 2,
            zIndex: 10,
          }}
        >
          {/* All-in-one Row */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 2,
                flexWrap: 'wrap',
              }}
            >
            {/* Left: AI Studio Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AIIcon sx={{ fontSize: 28, color: 'primary.main' }} />
              <Box>
                <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.2 }}>
                  AI Studio
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                  Ideogram v3 Turbo
                </Typography>
              </Box>
            </Box>

            {/* Status Chip */}
            <Chip
              label={aiServerStatus === 'online' ? 'API Ready' : 'Checking...'}
              color={aiServerStatus === 'online' ? 'success' : 'warning'}
              size="small"
              icon={aiServerStatus === 'online' ? undefined : <CircularProgress size={12} />}
              sx={{ height: 24 }}
            />

            {/* Info Alert (compact) */}
            <Alert severity="info" icon={<InfoIcon fontSize="small" />} sx={{ fontSize: '0.7rem', py: 0.25, px: 1, flexGrow: 1, minWidth: 200 }}>
              Configure prompts in "Ideogram Config" tab. Cloud AI. ~$0.03/image.
            </Alert>

            {/* Generate Professional Prompt Button */}
            <Button
              variant="outlined"
              size="medium"
              onClick={(event) => {
                event.stopPropagation();
                generatePrompt();
              }}
              disabled={generating}
              startIcon={generating ? <CircularProgress size={16} /> : null}
              sx={{ whiteSpace: 'nowrap', minWidth: 180 }}
            >
              {generating ? 'Generating...' : 'Generate Prompt'}
            </Button>

            {/* Ideogram Generate Button */}
            {onIdeogramGenerate && (
              <Button
                variant="contained"
                size="medium"
                onClick={(event) => {
                  event.stopPropagation();
                  onIdeogramGenerate();
                }}
                disabled={ideogramGenerating || !canIdeogramGenerate}
                startIcon={ideogramGenerating ? <CircularProgress size={16} color="inherit" /> : <AIIcon />}
                sx={{ whiteSpace: 'nowrap', minWidth: 180 }}
              >
                {ideogramGenerating ? 'Generating...' : 'Generate Images'}
              </Button>
            )}
          </Box>
        </Box>
      </Card>

      {/* Prompt Modal */}
      <Dialog open={showPromptModal} onClose={() => setShowPromptModal(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Generated Professional Prompt</Typography>
            <IconButton onClick={() => setShowPromptModal(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {generatedPrompt && (
            <Box
              sx={{
                bgcolor: 'background.default',
                p: 2,
                borderRadius: '6px',
                maxHeight: 500,
                overflowY: 'auto',
              }}
            >
              <pre
                style={{
                  margin: 0,
                  fontFamily: 'monospace',
                  fontSize: '13px',
                  whiteSpace: 'pre-wrap',
                  wordWrap: 'break-word',
                }}
              >
                {JSON.stringify(generatedPrompt, null, 2)}
              </pre>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPromptModal(false)}>Close</Button>
          <Button startIcon={<SaveIcon />} variant="outlined">
            Save Template
          </Button>
          <Button startIcon={<CopyIcon />} variant="contained" onClick={copyPrompt}>
            Copy Prompt
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PromptGeneratorPro;
